import { useRef, useState } from "react";
import { AlertCircle, Camera, CheckCircle, Loader2, MapPin, Send, Video, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";

const EMPTY = { description: "", location: "", contact_name: "", contact_phone: "" };

export const ReportNewsForm = ({ onSuccess, autoResetAfterSuccess = false }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [video, setVideo] = useState(null);
  const [location, setLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [formData, setFormData] = useState(EMPTY);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 5) {
      toast.error("Máximo 5 fotos permitidas");
      return;
    }
    setPhotos((prev) => [...prev, ...files.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("El video no debe superar 50MB");
      return;
    }
    setVideo({ file, preview: URL.createObjectURL(file) });
  };

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
        setGettingLocation(false);
        toast.success("Ubicación obtenida");
      },
      () => {
        setGettingLocation(false);
        toast.error("No se pudo obtener la ubicación");
      }
    );
  };

  const reset = () => {
    setFormData(EMPTY);
    setPhotos([]);
    setVideo(null);
    setLocation(null);
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      toast.error("Por favor describe la noticia");
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append("description", formData.description);
      data.append("location", formData.location);
      data.append("contact_name", formData.contact_name);
      data.append("contact_phone", formData.contact_phone);
      if (location) {
        data.append("lat", location.lat.toString());
        data.append("lng", location.lng.toString());
      }
      photos.forEach((p) => data.append("photos", p.file));
      if (video) data.append("video", video.file);

      await axios.post(`${API}/news-reports`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSuccess(true);
      onSuccess?.();
      if (autoResetAfterSuccess) setTimeout(reset, 2000);
    } catch (error) {
      toast.error("Error al enviar el reporte");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="py-10 text-center" data-testid="report-success">
        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">¡Reporte Enviado!</h3>
        <p className="text-gray-500 mb-6">
          Gracias por tu colaboración. Nuestro equipo revisará tu reporte.
        </p>
        <Button variant="outline" onClick={reset} data-testid="report-send-another">
          Enviar otro reporte
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="report-news-form">
      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">¿Qué está pasando? *</label>
        <Textarea
          placeholder="Describe la noticia con el mayor detalle posible..."
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={4}
          className="resize-none"
          data-testid="report-description"
        />
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">Fotos (máx. 5)</label>
        <div className="flex flex-wrap gap-2">
          {photos.map((photo, index) => (
            <div key={index} className="relative w-20 h-20">
              <img src={photo.preview} alt="" className="w-full h-full object-cover rounded-lg" />
              <button
                type="button"
                onClick={() => setPhotos((prev) => prev.filter((_, i) => i !== index))}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                data-testid={`report-remove-photo-${index}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {photos.length < 5 && (
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-red-400 transition-colors"
              data-testid="report-add-photo"
            >
              <Camera className="h-6 w-6 text-gray-400" />
            </button>
          )}
        </div>
        <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={handlePhotoSelect} className="hidden" />
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 mb-2 block">Video (máx. 50MB)</label>
        {video ? (
          <div className="relative">
            <video src={video.preview} className="w-full h-32 object-cover rounded-lg" controls />
            <button
              type="button"
              onClick={() => setVideo(null)}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
              data-testid="report-remove-video"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => videoInputRef.current?.click()}
            className="w-full h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center gap-2 hover:border-red-400 transition-colors"
            data-testid="report-add-video"
          >
            <Video className="h-6 w-6 text-gray-400" />
            <span className="text-gray-400">Agregar video</span>
          </button>
        )}
        <input ref={videoInputRef} type="file" accept="video/*" onChange={handleVideoSelect} className="hidden" />
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 block">Ubicación</label>
        <div className="flex gap-2">
          <Input
            placeholder="Ej: Frente al parque central"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            className="flex-1"
            data-testid="report-location"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={getLocation}
            disabled={gettingLocation}
            className={location ? "text-green-600 border-green-600" : ""}
            data-testid="report-gps"
          >
            {gettingLocation ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
          </Button>
        </div>
        {location && (
          <p className="text-xs text-green-600 mt-1">
            GPS: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Tu nombre (opcional)</label>
          <Input
            placeholder="Nombre"
            value={formData.contact_name}
            onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
            data-testid="report-contact-name"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">Teléfono (opcional)</label>
          <Input
            placeholder="9999-9999"
            value={formData.contact_phone}
            onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
            data-testid="report-contact-phone"
          />
        </div>
      </div>

      <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
        <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
        <p>Tu reporte será revisado por nuestro equipo antes de ser publicado.</p>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-red-600 hover:bg-red-700"
        data-testid="submit-report"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
        Enviar Reporte
      </Button>
    </form>
  );
};

export default ReportNewsForm;
